/** @format */

import {Widget, WidgetStat} from 'components/PropertyWidget/model';

import {ValueBox} from 'components/PropertyWidget/style';

import WidgetFormattedNumber from 'components/PropertyWidget/components/WidgetFormattedNumber';

import {CommonChartData} from 'components/charts/multiline-chart/model/charts';

import {sumSeries, sumSeriesToDate} from 'features/mediaplan-progress/helpers/sumSeries';
import {diff} from 'features/mediaplan-progress/helpers/diff';
import {ratioPercent} from 'features/mediaplan-progress/helpers/ratioPercent';

import {safeDivide} from 'helpers/numberHelpers';

import getTokenWithSpace from '../../helpers/getTokenWithSpace';
import {ComplexValue} from '../../models/common';
import {
  factSeriesFromChartData,
  lastFactKey,
  planSeriesFromChartData,
} from '../progress-adapter/seriesFromChartData';

export interface GetStatsTypeDOptions {
  data: CommonChartData[];
  day: number;
  month: string;
  to: string;
  widget: Widget<ComplexValue>;
}

const getStatsTypeD = ({day, to, month, data, widget}: GetStatsTypeDOptions): WidgetStat[] => {
  const {token: tokenRaw, roundNumberBy, needSpaceBeforeToken, isPositiveRed} = widget;
  const token = getTokenWithSpace({token: tokenRaw, needSpaceBeforeToken});

  const planSeries = planSeriesFromChartData(data);
  const factSeries = factSeriesFromChartData(data);

  const plan = sumSeries(planSeries);
  const fact = sumSeries(factSeries);
  const averageInDay = safeDivide(fact, Object.keys(factSeries).length);
  const planAtToday = sumSeriesToDate(planSeries, lastFactKey(data) ?? '');

  const execution = ratioPercent(fact, planAtToday);
  const executionCommon = ratioPercent(fact, plan);

  const planAtTodayHeading = `План ${to} ${day} ${month}`;
  const factAtTodayHeading = `Факт ${to} ${day} ${month}`;

  const diffToday = diff({plan: planAtToday, fact});
  const diffPercent = ratioPercent(diffToday, planAtToday);

  const diffCommon = diff({plan, fact});
  const diffPercentCommon = ratioPercent(diffCommon, plan);

  return [
    {
      heading: (
        <>
          План
          <br />
          недоступен
        </>
      ),
      showConditions: ['unreachable'],
    },
    {
      heading: 'План',
      ValueComponent: (
        <ValueBox>
          <WidgetFormattedNumber number={plan} roundNumberBy={roundNumberBy} postfix={token} />
        </ValueBox>
      ),
      showConditions: ['common'],
    },
    {
      heading: 'Факт',
      ValueComponent: (
        <ValueBox>
          <WidgetFormattedNumber number={fact} roundNumberBy={roundNumberBy} postfix={token} />
          <WidgetFormattedNumber
            number={diffCommon}
            roundNumberBy={0}
            postfix={token}
            colorize
            isPositiveRed={isPositiveRed}
            showPlusMinus
          />
          <WidgetFormattedNumber
            number={diffPercentCommon}
            roundNumberBy={1}
            colorize
            isPositiveRed={isPositiveRed}
            showPlusMinus
            postfix='%'
          />
        </ValueBox>
      ),
      showConditions: ['common'],
    },
    {
      heading: planAtTodayHeading,
      ValueComponent: (
        <ValueBox>
          <WidgetFormattedNumber
            number={planAtToday}
            roundNumberBy={roundNumberBy}
            postfix={token}
          />
        </ValueBox>
      ),
      showConditions: ['future'],
    },
    {
      heading: factAtTodayHeading,
      ValueComponent: (
        <ValueBox>
          <WidgetFormattedNumber number={fact} roundNumberBy={roundNumberBy} postfix={token} />
          <WidgetFormattedNumber
            number={diffToday}
            roundNumberBy={0}
            postfix={token}
            colorize
            isPositiveRed={isPositiveRed}
            showPlusMinus
          />
          <WidgetFormattedNumber
            number={diffPercent}
            roundNumberBy={1}
            colorize
            isPositiveRed={isPositiveRed}
            showPlusMinus
            postfix='%'
          />
        </ValueBox>
      ),
      showConditions: ['future'],
    },
    {
      heading: 'Выполнение',
      ValueComponent: (
        <ValueBox>
          <WidgetFormattedNumber number={executionCommon} roundNumberBy={1} postfix='%' />
        </ValueBox>
      ),
      showConditions: ['common'],
    },
    {
      heading: 'Выполнение',
      ValueComponent: (
        <ValueBox>
          <WidgetFormattedNumber number={execution} roundNumberBy={1} postfix='%' />
        </ValueBox>
      ),
      showConditions: ['future'],
    },
    {
      heading: 'Ср. в день',
      ValueComponent: (
        <ValueBox>
          <WidgetFormattedNumber
            number={averageInDay}
            roundNumberBy={roundNumberBy}
            postfix={token}
          />
        </ValueBox>
      ),
      showConditions: ['not-kpi', 'unreachable'],
    },
    {
      heading: 'За период',
      ValueComponent: (
        <ValueBox>
          <WidgetFormattedNumber number={fact} roundNumberBy={roundNumberBy} postfix={token} />
        </ValueBox>
      ),
      showConditions: ['not-kpi', 'unreachable'],
    },
  ];
};

export default getStatsTypeD;
